import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildProjectPath, FOLDER_BASE, parseProjectLocation, type ProjectLocation, type ProjectPathParams
} from './projectPath';

export interface ProjectRoute extends ProjectLocation {
  /**
   * Builds a URL in the family the caller is already in, keeping the base and the folder
   * prefix. Every in-place navigation goes through this, so no component has to know
   * whether it is rendering inside the app or the viewer, or where the project lives.
   */
  buildPath: (params?: Partial<Omit<ProjectPathParams, 'projectId'>> & { projectId?: string }) => string;
}

const emptyLocation: ProjectLocation = { base: FOLDER_BASE, folderPath: '', tab: 'overview' };

/** Reads the project URL from the location itself, so it works at any depth of the tree. */
export const useProjectRoute = (): ProjectRoute => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const location = parseProjectLocation(pathname) ?? emptyLocation;

    return {
      ...location,
      buildPath: (params = {}) => buildProjectPath({
        base: location.base,
        folderPath: location.folderPath,
        projectId: location.projectId ?? '',
        ...params,
      }),
    };
  }, [pathname]);
};
